import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import PageGray from '../../components/General/PageGray';
import H1 from '../../components/Typography/Headings/H1';
import H2 from '../../components/Typography/Headings/H2';
import P from '../../components/Typography/P';
import Span from '../../components/Typography/Span';
import { Button } from '@testbusters/button';
import { useGetSurveys, useGetSurveyResults, type ApiSurvey } from '../../hooks/surveys/useSurveys';
import SurveyResultsBoard from '../../components/Survey/SurveyResultsBoard';
import SurveyPromoCard from '../../components/Survey/SurveyPromoCard';
import SurveyRuleBanners from '../../components/Survey/SurveyRuleBanners';
import Loader from '../../components/Loader/Loader';
import illustration from '/illustrations/directions.svg';

export const Route = createFileRoute('/_sidebar/orientamento/')({
  component: OrientamentoPage,
});

function OrientamentoPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { surveys, isFetching } = useGetSurveys();

  const survey = surveys[0];
  const isCompleted = !!survey?.compiledAt;

  const handleStart = () => {
    if (!survey) return;
    navigate({ to: '/orientamento/$surveyId', params: { surveyId: survey._id } });
  };

  if (isFetching) return <Loader fullScreen />;

  if (survey && isCompleted) {
    return (
      <CompletedOrientamento
        survey={survey}
        onSeeRecap={() =>
          navigate({
            to: '/orientamento/$surveyId',
            params: { surveyId: survey._id },
            search: { view: 'summary' },
          })
        }
        onRepeat={handleStart}
        formattedDate={new Date(survey.compiledAt!).toLocaleDateString(i18n.language)}
      />
    );
  }

  const sections = [
    {
      title: t('orientamento.sections.professioni'),
      description: t('orientamento.sections.professioniDesc'),
    },
    {
      title: t('orientamento.sections.attitudiniPersonali'),
      description: t('orientamento.sections.attitudiniPersonaliDesc'),
    },
    {
      title: t('orientamento.sections.attitudiniLavorative'),
      description: t('orientamento.sections.attitudiniLavorativeDesc'),
    },
  ];

  return (
    <PageGray className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <H1>{t('nav.orientamentoUniversitario')}</H1>
        <P>{t('orientamento.subtitle')}</P>
      </div>

      <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-8 max-w-3xl min-w-full">
        <img src={illustration} alt="" className="w-28 h-28 object-contain" />

        <H2 className="text-center">{t('orientamento.cardTitle')}</H2>

        <P className="text-center">
          {t('orientamento.cardDescription')}{' '}
          <Span className="font-bold">{t('orientamento.cardDescriptionBold')}</Span>
        </P>

        <P className="text-center">{t('orientamento.sectionsLabel')}</P>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 w-full">
          {sections.map((section) => (
            <div
              key={section.title}
              className="flex flex-col items-center gap-1 text-center"
            >
              <Span className="font-bold">{section.title}</Span>
              <Span className="text-text-body-description text-sm">{section.description}</Span>
            </div>
          ))}
        </div>

        <P className="text-center text-sm text-text-body-description">
          {t('orientamento.disclaimer')}
        </P>

        <Button variant="primary" onClick={handleStart} disabled={!survey} className="min-w-48">
          {t('orientamento.startButton')}
        </Button>
      </div>
    </PageGray>
  );
}

function CompletedOrientamento({
  survey,
  formattedDate,
  onSeeRecap,
  onRepeat,
}: {
  survey: ApiSurvey;
  formattedDate: string;
  onSeeRecap: () => void;
  onRepeat: () => void;
}) {
  const { t } = useTranslation();
  const { results, isFetching } = useGetSurveyResults(survey._id);

  return (
    <PageGray className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <H1>{t('nav.orientamentoUniversitario')}</H1>
        <P>{t('orientamento.subtitle')}</P>
      </div>

      {/* Result summary banner */}
      <div className="bg-white rounded-2xl p-6 lg:p-8 flex flex-col gap-6">
        <H2 className="text-text-body-primary">
          {t('orientamento.results.resultDate', { date: formattedDate })}
        </H2>
        <div className="flex flex-col sm:flex-row gap-6">
          <img src={illustration} alt="" className="w-24 h-24 object-contain shrink-0" />
          <div className="flex flex-col gap-4 flex-1">
            <P className="text-text-body-description">
              {t('orientamento.results.resultLead')}{' '}
              <Span className="font-bold text-text-body-primary">
                {t('orientamento.results.resultLeadBold')}
              </Span>
            </P>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <Button variant="secondary" onClick={onSeeRecap}>
                {t('orientamento.results.seeRecap')}
              </Button>
              <Button variant="primary" onClick={onRepeat}>
                {t('orientamento.results.repeatQuestionnaire')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isFetching && !results ? (
        <Loader />
      ) : results ? (
        <>
          <SurveyResultsBoard results={results} showSectionHeading />
          <SurveyPromoCard variant="full" />
          <SurveyRuleBanners results={results} />
        </>
      ) : null}
    </PageGray>
  );
}
